import React, { RefObject } from 'react'
import { ActivityIndicator, View, ScrollView, Text, Image, StyleSheet, Dimensions, TouchableOpacity, Platform, findNodeHandle, Linking } from 'react-native'
import { QuestionGroup } from './question-group'
import { ProductDisplay } from './product-display'
import { ProductOptions } from './product-options'
import { Badge } from './badge'
import BUILD_IMG from './build.png'
import PropTypes from 'prop-types'
import { isPortrait } from '../orientation'
import { Node } from '../types/node'
import { Group } from '../types/group'
import { Searchbar } from 'react-native-paper'
import axios from 'axios'
import { REACT_NATIVE_APIV2 } from '../constants'

const BUILD_IMG_HEIGHT = 60

type ConfiguratorV2Props = {
  /** The full product SKU */
  sku: string | null,
  /** The full product SKU specific for the partner */
  customSKU: string | null,
  /** The base product SKU */
  baseSKU: string | null,
  /** The catalog. */
  catalog: string,
  /** The partner code. */
  partner: string,
  displayName: string,
  /** The product page URL. */
  productURL: string,
  /** The primary color. */
  accentColor: string,
  /** The product color title text. */
  color: string | null,
  /** The SKU specific product description. */
  description: string | null,
  /** The list of objects describing each option. */
  optionGroups?: Array<Group>,
  /** All of the nodes in the tree. */
  treeData: Array<Node>,
  /** The currently selectd path through the tree of nodes. */
  path?: Array<Node>,
  /** The first question. */
  initialQuestions: Array<Node>,
  /** The question nodes */
  questions?: Array<Node>,
  /** true if should show the mobile product display */
  showProductDisplay: boolean,
  /** The selected answer to the root question. */
  selectedRootAnswer: string | null,
  /** Callback for when the answer is selected. */
  onAnswerChange: Function,
  /** Set the entire yLocation array. */
  setYLocations: Function,
  /** The onclick handler for an option change button aka a `Swatch`. */
  onSwatchChange: Function,
  /** The onclick handler for the image buttons. */
  onImageSwatchChange: Function,
  /**
   * Callback for when the height switch is clicked.
   * Used to differentiate between a click on the swatch and a click on the answer component.
   */
  onSecondaryHeightSwitchClicked: Function,
  /** Reset the configurator */
  onReset: Function,
  /** Opens the quote cart. */
  openQuoteCart: Function,
  extraAttributes: object,
  /** Initial sku to render options for. */
  initSKU: string | null,
  /** Function to set initSKU. Takes in a sku string */
  setInitSKU: Function,
  /** Function to set the leaf node in ConfiguratorV2CTN */
  setLeafNode: Function,
}


export const ConfiguratorV2 = ({ sku, customSKU, baseSKU, catalog, partner, displayName, productURL, accentColor, color, description, optionGroups, treeData, path, initialQuestions, questions, selectedRootAnswer, showProductDisplay, onAnswerChange, setYLocations, onSwatchChange, onImageSwatchChange, onSecondaryHeightSwitchClicked, onReset, openQuoteCart, extraAttributes, initSKU, setInitSKU, setLeafNode }: ConfiguratorV2Props) => {
  const [groupRefs, setGroupRefs] = React.useState<Map<number, RefObject<any>>>(new Map<number, RefObject<any>>())
  const [specsSpinner, setSpecsSpinner] = React.useState(false)
  const configuratorRef = React.useRef()

  React.useEffect(() => {
    if (baseSKU) {
      const keys = path?.filter((node: Node) => node.type !== 'leaf' && node.type !== 'title-only' && node.type !== 'title-&-image').length
      if (keys) {
        const tmpGroupRefs = new Map<number, RefObject<any>>(groupRefs)
        for (var key = 0; key < keys! + 2; key += 1) {
          const ref = groupRefs.get(key) ?? React.createRef()
          tmpGroupRefs.set(key, ref)
        }

        setGroupRefs(tmpGroupRefs)
      }

    }
  }, [baseSKU])

  const getActive = (id: string) => {
    return treeData
      .filter(node => node.type !== 'leaf')
      .filter(dataNode => dataNode.parent === id)
      .sort((a, b) => a.index - b.index)
      .find(node => node.active === true)?.id ?? null
  }

  const getShowHeightOptions = () => {
    return path?.find(node => node.name === 'Desk Height (29"-31")' || node.name === 'Medium Bench (32"-36")' || node.name === 'High Bench (37"-42")') !== undefined ? true : false
  }

  // Shell chairs don't have a High bench variant
  const getShowHeightOptionsShellChairs = () => {
    return path?.find(node => node.name.includes('SDHCH') || node.name.includes('SMBCH')) !== undefined ? false : true
  }

  const getSelectedHeight = () => {
    return lookupIndex(path?.find(node => node.name === 'Desk Height (29"-31")' || node.name === 'High Bench (37"-42")' || node.name === 'Medium Bench (32"-36")')?.name)
  }

  const lookupIndex = (name?: string): number => {
    if (name?.includes('Desk Height')) {
      return 0
    } else if (name?.includes('Medium Bench')) {
      return 1
    } else if (name?.includes('High Bench')) {
      return 2
    } else {
      return 0
    }
  }

  const onHeightChange = (key: number) => {
    const parent = path?.find(node => node.attributes.question === 'How tall is your work surface?')
    if (parent) {
      const parentId = parent!.id
      const id = (treeData.filter(node => node.parent === parentId).sort((a, b) => a.index - b.index))[key].id
      onSecondaryHeightSwitchClicked(true)
      onAnswerChange(id)
    }
  }

  const onSpecsPDFNavigate = () => {
    setSpecsSpinner(true)
    const pdfURL = 'https://pdfgen-ecom.herokuapp.com'

    const url = pdfURL + '?sku=' + sku
      + '&catalog=' + catalog
      + '&partner=' + partner.toLowerCase()
    if (Platform.OS === 'web') {
      window.open(url)
    } else {
      Linking.openURL(url)
    }
    setSpecsSpinner(false)
  }

  const searchForSKU = () => {
    const baseURL = REACT_NATIVE_APIV2
    axios.get(`${baseURL}/server.php/api/alternateSkuCross/?sku=${initSKU}`)
    .then((response) => {
      let res = response.data;
      let sku = res.sku;
      let family = res.base_sku;
      const node = treeData.find(node => {
        return family === node.name;
      });
      if (node) {
        node.attributes.defaultSKU = sku;
        setLeafNode(node);
      }
    })
  }


  const styles = StyleSheet.create({
    configurator: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: Platform.OS === 'web' && isPortrait() ? 60 : 0,
    },
    topbar: {
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    buildImage: {
      height: 64,
      width: 64,
      margin: 'auto',
      marginTop: 40,
    },
    brandContainer: {
      display: 'flex',
      flexDirection: 'column',
      // flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      margin: 'auto',
      marginTop: 25,
      marginBottom: 25,
    },
    introText: {
      textAlign: 'center',
      fontSize: 24,
      maxWidth: 600,
      margin: 'auto',
      marginTop: 50,
      marginBottom: 50,
    },
    secondaryBtnCtn: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    titleText: {
      fontSize: 32,
      color: '#111',
      fontWeight: 'bold',
      textAlign: 'center',
    },
    byText: {
      textAlign: 'center',
      fontSize: 18,
      marginTop: 10,
      marginBottom: 10,
    },
    brandText: {
      fontSize: 24,
      color: accentColor,
      fontWeight: 'bold',
      textAlign: 'center',
    },
    primaryButtonView: {
      marginLeft: 'auto',
      marginRight: 'auto',
      marginBottom: 40,
      marginTop: 40,
      width: '90%'
    },
    primaryButton: {
      marginBottom: 20,
      padding: 10,
      width: '100%',
      borderRadius: 5,
      backgroundColor: '#C4C4C4'
    }
  })

  return (

    <View

      style={styles.configurator}
      ref={configuratorRef as any}
      onLayout={async e => {
        const promises = []

        for (const [key, ref] of groupRefs) {
          if (ref.current) {

            const refPromise = new Promise((res, rej) => {
              if (Platform.OS === 'ios' || Platform.OS === 'web') {
                ref?.current?.measure((x: number, y: number, width: number, height: number, pageX: number, pageY: number) => {
                  res({ key: key, y: y })
                })
              } else {
                ref.current.measureLayout(
                  findNodeHandle(configuratorRef.current ?? null),
                  (x: number, y: number, width: number, height: number, pageX: number, pageY: number) => {
                    res({ key: key, y: y })
                  }
                )
              }
            })

            promises.push(refPromise)

          }
        }

        const yLocationsArray = await Promise.all(promises) as Array<any>
        const yLocations = new Map<number, number>()

        for (const row of yLocationsArray) {
          yLocations.set(row.key, row.y)
        }

        if (yLocations.size > 0) {
          setYLocations(yLocations)
        }


      }}
    >{console.log('refreshing data?')}
      <View
        style={styles.topbar}
      >
        <Image
          style={styles.buildImage}
          height={BUILD_IMG_HEIGHT}
          width={BUILD_IMG_HEIGHT}
          source={BUILD_IMG as any}
        />
      </View>
      <View
        style={styles.brandContainer}
      >
        {
          isPortrait() == false &&
          <Text style={styles.titleText}>Build A Chair</Text>
        }

        <Text style={styles.byText}>by</Text>
        <Text style={styles.brandText}>{displayName}</Text>
      </View>
      <View>
        {showProductDisplay && <Searchbar
          placeholder='Enter product SKU'
          onChangeText={setInitSKU}
          value={initSKU}
          onIconPress={searchForSKU}
          style={{ width: '40%', alignSelf:'center' }}
        />}
        {!showProductDisplay && <Searchbar
          placeholder='Enter product SKU'
          onChangeText={setInitSKU}
          value={initSKU}
          onIconPress={searchForSKU}
          style={{ width: '90%', alignSelf:'center' }}
        />}
      </View>

      <QuestionGroup
        viewRef={groupRefs.get(0)}
        keyID={0}
        question={'Choose your environment to get started!'}
        questions={initialQuestions}
        onAnswerChange={onAnswerChange}
        active={selectedRootAnswer}
        accentColor={accentColor}
        extraAttributes={extraAttributes}
      />
      {
        questions?.map((q, key) => {
          return (
            <QuestionGroup
              viewRef={groupRefs.get(key + 1)}
              key={key + 1}
              keyID={key + 1}
              questions={treeData
                .filter(node => node.type !== 'leaf')
                .filter(dataNode => dataNode.parent === q.id)}
              question={q?.attributes?.question ?? q.name}
              path={path}
              onAnswerChange={onAnswerChange}
              active={getActive(q.id)}
              accentColor={accentColor}
              extraAttributes={extraAttributes}
            />
          )
        })
      }
      {
        !selectedRootAnswer &&
        <Text style={styles.introText}>Build the best chair for your environment with our app now! Please reach out if you have any questions!</Text>
      }
      {
        (showProductDisplay === true && sku) &&
        <ProductDisplay
          viewRef={isPortrait() ? groupRefs.get(Math.max(...[...groupRefs].map(a => a[0])) - 1) : null}
          onImageLayout={() => { }}
          defaultSKU={sku}
          customSKU={customSKU}
          color={color}
          partner={partner}
        />
      }
      <ProductOptions
        sku={sku}
        viewRef={isPortrait() ? null : groupRefs.get(Math.max(...([...groupRefs].map(a => a[0]).map((key: number) => key))) - 1)}
        accentColor={accentColor}
        optionGroups={optionGroups}
        onImageSwatchChange={onImageSwatchChange}
        onSwatchChange={onSwatchChange}
        selectedHeight={getSelectedHeight()}
        showHeightOptions={getShowHeightOptions()}
        showHeightOptionsShellChairs={getShowHeightOptionsShellChairs()}
        onHeightChange={onHeightChange}
        partner={partner}
        onMobile={showProductDisplay}
      />
      {
        selectedRootAnswer &&
        <View style={styles.primaryButtonView}>
          {
            (extraAttributes['hideProductPageButton'] != true) &&
            <TouchableOpacity onPress={e => {
              if (partner == 'neta') {
                var url;
                if (customSKU != null) {
                  if (sku.includes('SO') || sku.includes('SL')) {
                    url = 'https://www.netascientific.com/lab-stools/' + customSKU
                  } else {
                    url = 'https://www.netascientific.com/lab-chairs/' + customSKU
                  }
                } else {
                  url = 'https://www.netascientific.com/contact-us'
                }


                if (Platform.OS === 'web') {
                  window.open(url)
                } else {
                  Linking.openURL(url)
                }
              } else {
                const url = productURL + baseSKU + '#' + sku

                if (Platform.OS === 'web') {
                  window.open(url)
                } else {
                  Linking.openURL(url)
                }
              }

            }} style={{ marginBottom: 20, padding: 10, backgroundColor: '#111' }}>
              <Text style={{ textAlign: 'center', color: 'white', fontSize: 20 }}>{(partner == 'neta' && customSKU == null) ? 'Contact Us' : 'View Product Page'}</Text>
            </TouchableOpacity>


          }
          <View style={styles.secondaryBtnCtn}>
            {
              (extraAttributes['hideQuoteRequest'] != true)
                ? <TouchableOpacity onPress={e => {
                  onSpecsPDFNavigate()
                }} style={{ marginBottom: 20, padding: 10, maxWidth: '45%', width: '100%', borderRadius: 5, backgroundColor: '#C4C4C4' }}>
                  {
                    specsSpinner
                      ? <ActivityIndicator />
                      : <Text style={{ textAlign: 'center', color: 'black', fontSize: 16 }}>Specifications</Text>
                  }
                </TouchableOpacity>
                : <TouchableOpacity onPress={e => {
                  onSpecsPDFNavigate()
                }} style={styles.primaryButton}>
                  {
                    specsSpinner
                      ? <ActivityIndicator />
                      : <Text style={{ textAlign: 'center', color: 'black', fontSize: 16 }}>Specifications</Text>
                  }
                </TouchableOpacity>
            }
            {
              (extraAttributes['hideQuoteRequest'] != true) && <TouchableOpacity onPress={async e => {
                await openQuoteCart(true)
              }} style={{ marginBottom: 20, padding: 10, maxWidth: '45%', width: '100%', borderRadius: 5, backgroundColor: '#C4C4C4' }}>
                <Text style={{ textAlign: 'center', color: 'black', fontSize: 16 }}>Quote Request</Text>
              </TouchableOpacity>
            }
          </View>
          <Text style={{ margin: 'auto', marginTop: 40, marginBottom: 40, fontWeight: 'bold', fontSize: 20, letterSpacing: 1, lineHeight: 30 }}>{description}</Text>
        </View>
      }
      <View style={{ marginLeft: 'auto', marginRight: 'auto', marginBottom: 40, marginTop: 0, width: '90%' }}>
        <TouchableOpacity onPress={e => {
          onReset()
        }} style={{ marginBottom: 20, padding: 10, backgroundColor: selectedRootAnswer ? 'slategray' : '#222', borderRadius: 5, }}>
          <Text style={{ textAlign: 'center', color: 'white', fontSize: 20, padding: 5 }}>Reset Configurator</Text>
        </TouchableOpacity>
        {
          selectedRootAnswer &&
          <Text style={{ textAlign: "center" }}>Or scroll up to choose new options.</Text>
        }

      </View>
    </View>
  )

}

ConfiguratorV2.propTypes = {
  sku: PropTypes.string,
  customSKU: PropTypes.string,
  baseSKU: PropTypes.string,
  catalog: PropTypes.string.isRequired,
  partner: PropTypes.string.isRequired,
  productURL: PropTypes.string,
  accentColor: PropTypes.string.isRequired,
  color: PropTypes.string,
  description: PropTypes.string,
  optionGroups: PropTypes.array,
  treeData: PropTypes.array.isRequired,
  path: PropTypes.array,
  initialQuestions: PropTypes.array.isRequired,
  questions: PropTypes.array,
  selectedRootAnswer: PropTypes.string,
  showProductDisplay: PropTypes.bool.isRequired,
  onAnswerChange: PropTypes.func.isRequired,
  setYLocations: PropTypes.func.isRequired,
  onSwatchChange: PropTypes.func.isRequired,
  onImageSwatchChange: PropTypes.func.isRequired,
  onSecondaryHeightSwitchClicked: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  openQuoteCart: PropTypes.func.isRequired,
}

